import { NoResultsWithButton, OverviewHeader, RestrictedByTooltip, SearchInput } from "components";
import { Button } from "components/buttons/button/Button";
import { RolePermissions, RouteNames, SortingDirection } from "enums";
import { useHasPermissions } from "features/Application/hooks";
import React, { useCallback, useEffect, useMemo, useRef } from "react";
import GenericItemsView from "views/ItemsView/GenericItemsView";
import { columnToParamMap, getColumnOptions, TemplatesOverviewColumns } from "./columnOptions";
import { type AppDispatch, type RootState } from "features/Application/globaltypes/redux";
import { bindAction } from "interfaces";
import { connect, type ConnectedProps } from "react-redux";
import { reset } from "../state/TemplateOverviewSlice";
import {
  approveTemplate,
  declineTemplate,
  deleteTemplate,
  fetchTemplates,
} from "../state/thunks/TemplateOverviewThunk";
import { TemplatesFilterForm } from "./TemplatesFilterForm";
import { resetAppliedFilter, resetFilter, setAppliedFilter, setSearch } from "../state/TemplateFiltersSlice";
import classNames from "classnames";
import { useNavigate } from "react-router";
import { isEmpty } from "lodash";
import { faBuildingUser } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Observable } from "utils";
import { ProcessTemplateRequestModal } from "./ProcessTemplateRequestModal";

import { DeleteTemplateModal } from "./DeleteTemplateModal";
import { type TemplateOverview, TemplateStatusEnum } from "../types/models";
import {
  ApproveAccountTemplateSuccess,
  DeclineAccountTemplateSuccess,
} from "features/Application/services/realTimeNotification/events/people/peopleEvents";

const isApproved = (item: TemplateOverview): boolean => item.status === TemplateStatusEnum.Approved;
const isRequested = (item: TemplateOverview): boolean => item.status === TemplateStatusEnum.Pending;

type Props = PropsFromRedux & {
  renderContentSwitcher?: () => React.ReactElement;
  onlyOwn?: boolean;
  title?: string;
  withCreate?: boolean;
  hiddenColumns?: TemplatesOverviewColumns[];
};

export const TemplatesOverview = (props: Props) => {
  const {
    title = "Groups",
    fetchTemplates,
    items,
    itemsCount,
    isLoading,
    resetOverview,
    onSearchChanged,
    applyFilter,
    resetAppliedFilter,
    filters,
    approveTemplate,
    declineTemplate,
    deleteTemplate,
    onlyOwn = false,
    withCreate = true,
    hiddenColumns,
  } = props;
  const hasManagePermission = useHasPermissions([RolePermissions.TemplatesManage]);
  const hasGroupManagePermission = useHasPermissions([RolePermissions.GroupsManage]);
  const reloadListItemsRef = useRef<(enableSorting: boolean) => void>();
  const isSearchEmpty = isEmpty(filters.search);
  const navigate = useNavigate();

  const processTemplateRequestObserver = useMemo(
    () => new Observable<(onSubmit: () => void, approve: boolean, owner: string) => void>(),
    [],
  );
  const deleteObserver = useMemo(() => new Observable<(onSubmit: () => void) => void>(), []);

  useEffect(() => {
    return () => {
      resetOverview();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearchChanged = (search: string) => {
    onSearchChanged(search);
    reloadListItemsRef.current?.(isEmpty(search));
  };

  const handleFetch = useCallback(
    (
      skip: number = 0,
      top: number = 10,
      sortingColumnName: string = TemplatesOverviewColumns.Added,
      sortingDirection: SortingDirection = SortingDirection.Descending,
    ) => {
      const sortBy = columnToParamMap[sortingColumnName.toLowerCase() as Lowercase<TemplatesOverviewColumns>];
      fetchTemplates(skip, top, sortBy, sortingDirection, onlyOwn);
    },
    [fetchTemplates, onlyOwn],
  );

  const handleEdit = useCallback(
    (id: number) => {
      navigate(`${id}`);
    },
    [navigate],
  );

  const handleTitleClick = useCallback((item: TemplateOverview) => {
    const { isOwn, id } = item;
    if (isOwn) {
      return `${id}`;
    } else if (isApproved(item)) {
      return `/${RouteNames.peopleGroups}/${id}`;
    } else {
      return `${RouteNames.preview}/${id}/${RouteNames.enrollment}`;
    }
  }, []);

  const processTemplate = useCallback(
    (id: number, approve: boolean, owner: string) => () => {
      processTemplateRequestObserver.notify(
        () => {
          if (approve) {
            approveTemplate(id, owner, () => reloadListItemsRef.current?.(isSearchEmpty));
          } else {
            declineTemplate(id, owner, () => reloadListItemsRef.current?.(isSearchEmpty));
          }
        },
        approve,
        owner,
      );
    },
    [approveTemplate, declineTemplate, isSearchEmpty, processTemplateRequestObserver],
  );

  const handleDelete = useCallback(
    (id: number) => () => {
      deleteObserver.notify(() => {
        deleteTemplate(id, () => reloadListItemsRef.current?.(isSearchEmpty));
      });
    },
    [deleteObserver, deleteTemplate, isSearchEmpty],
  );

  const columnOptions = useMemo(() => {
    return getColumnOptions({
      handleEdit,
      handleTitleClick,
      hasManagePermission,
      hasGroupManagePermission,
      processTemplate,
      handleDelete,
      onlyOwn,
      hiddenColumns: hiddenColumns ?? [],
    });
  }, [
    handleEdit,
    handleTitleClick,
    hasManagePermission,
    hasGroupManagePermission,
    processTemplate,
    handleDelete,
    onlyOwn,
    hiddenColumns,
  ]);

  const renderCreateButton = () => {
    return (
      <RestrictedByTooltip hasPermission={hasManagePermission}>
        <Button
          primary
          className="create-button"
          onClick={() => navigate("add-template")}
          disabled={!hasManagePermission}
        >
          Create Template
        </Button>
      </RestrictedByTooltip>
    );
  };

  const renderNoResults = () => {
    const title = withCreate ? "You don't have any Templates" : "You don't have any Inherited Groups";
    const description = withCreate
      ? "Looks like you don't have any Templates."
      : "Looks like you don't have any Inherited Groups.";
    return (
      <NoResultsWithButton
        title={title}
        description={description}
        icon={<FontAwesomeIcon icon={faBuildingUser} className="no-results-icon" />}
        actionButton={withCreate ? renderCreateButton() : undefined}
        filtered={!isEmpty(filters.appliedFilter) || !isEmpty(filters.search)}
      />
    );
  };

  return (
    <section className={classNames("nested-content")}>
      <OverviewHeader title={title} itemName="Template">
        {withCreate && renderCreateButton()}
      </OverviewHeader>
      <GenericItemsView
        items={items}
        isLoading={isLoading}
        columnOptions={columnOptions}
        fetchData={handleFetch}
        dataCount={itemsCount}
        isSelectDisabled={() => true}
        customHeaderContent={props.renderContentSwitcher?.()}
        renderSearch={() => <SearchInput placeholder="Search for Templates..." onChange={handleSearchChanged} />}
        // @ts-ignore
        getFilterForm={() => <TemplatesFilterForm />}
        applyFilter={applyFilter}
        resetFilter={resetAppliedFilter}
        setReloadListItems={(reloadListItems) => (reloadListItemsRef.current = reloadListItems)}
        appliedFilter={filters.appliedFilter}
        noResultsContent={renderNoResults()}
        listViewRtnEvents={[DeclineAccountTemplateSuccess, ApproveAccountTemplateSuccess]}
      />
      <ProcessTemplateRequestModal processTemplateRequestObserver={processTemplateRequestObserver} />
      <DeleteTemplateModal deleteObserver={deleteObserver} />
    </section>
  );
};

/* istanbul ignore next */
const mapStateToProps = (state: RootState) => {
  const { overview, filters } = state.people.groupTemplate;

  return {
    ...overview,
    filters,
  };
};

const mapDispatchToProps = (dispatch: AppDispatch) => {
  return {
    resetOverview: () => {
      dispatch(reset());
      dispatch(resetFilter());
    },
    fetchTemplates: bindAction(fetchTemplates, dispatch),
    onSearchChanged: bindAction(setSearch, dispatch),
    applyFilter: bindAction(setAppliedFilter, dispatch),
    resetAppliedFilter: bindAction(resetAppliedFilter, dispatch),
    approveTemplate: bindAction(approveTemplate, dispatch),
    declineTemplate: bindAction(declineTemplate, dispatch),
    deleteTemplate: bindAction(deleteTemplate, dispatch),
  };
};
const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(TemplatesOverview);
export default ConnectedComponent;
